import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, Toast } from 'antd-mobile'
import getCaptcha from 'dynamic-tencent-captcha'
import { checkThreeFactor, create, detectFaceidAuth } from '../../api/orderService'
import { TemplateMerchantInterface } from '../../typings/orderTypes'
import { USER_TOKEN_KEY } from '../../constants/localStorageConstants'

interface FormValues {
  name: string
  phone: string
  idCard: string
  smsCode: string
}

interface UseOrderCreationProps {
  linkId: string
  shouldUseFaceId: boolean
  template: TemplateMerchantInterface | undefined
  getRoutersString: () => string
  getWechatAuthAppId: () => string | undefined
  getAlipayAuthAppId: () => string | undefined
}

interface UseOrderCreationResult {
  loading: boolean
  createOrder: (values: FormValues) => void
}

export default function useOrderCreation({
  linkId,
  shouldUseFaceId,
  template,
  getRoutersString,
  getWechatAuthAppId,
  getAlipayAuthAppId,
}: UseOrderCreationProps): UseOrderCreationResult {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  const handleFaceIdAuth = (values: FormValues, ticket: string, randstr: string) => {
    detectFaceidAuth({
      name: values.name,
      phone: values.phone,
      idCard: values.idCard,
      linkId: linkId,
      ticket: ticket,
      randstr: randstr,
    })
      .then((res) => {
        setLoading(false)
        if (res?.url) {
          window.location.href = res.url
        }
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const handleOrderCreate = (values: FormValues, ticket: string, randstr: string) => {
    create({
      ...values,
      linkId: linkId,
      ticket: ticket,
      randstr: randstr,
    })
      .then((data) => {
        localStorage.setItem(USER_TOKEN_KEY, data.token?.tokenValue)
        if (data.status === 35) {
          Dialog.alert({
            content: '请联系代理商确认报告。',
            onConfirm: () => {},
          }).then()
          return
        }
        navigate(
          '/payment?orderId=' +
            data.orderId +
            '&templateName=' +
            template?.name +
            '&templatePrice=' +
            template?.price +
            '&wxAppId=' +
            getWechatAuthAppId() +
            '&alipayAppId=' +
            getAlipayAuthAppId() +
            '&routers=' +
            getRoutersString()
        )
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false)
      })
  }

  const handleThreeFactorCheck = (values: FormValues, ticket: string, randstr: string) => {
    checkThreeFactor({
      ...values,
      ticket: ticket,
      randstr: randstr,
    }).then((checkRes) => {
      if (checkRes !== '1') {
        setLoading(false)
        Dialog.show({
          title: '信息核验',
          content: <div style={{ padding: '8px 0' }}>检测到姓名，身份证号和手机号不一致，请选择以下方式继续。</div>,
          closeOnAction: true,
          actions: [
            [
              {
                key: 'edit',
                text: '修改基本信息',
                style: {
                  color: '#666666',
                  background: '#f5f5f5',
                },
              },
              {
                key: 'face',
                text: '人脸识别核验',
                style: {
                  color: '#ffffff',
                  background: '#1677ff',
                },
                onClick: () => {
                  detectFaceidAuth({
                    ...values,
                    linkId: linkId,
                    ticket: ticket,
                    randstr: randstr,
                  }).then((res) => {
                    if (res?.url) {
                      window.location.href = res.url
                    }
                  })
                },
              },
            ],
          ],
        })
        return
      }
      handleOrderCreate(values, ticket, randstr)
    })
  }

  const createOrder = (values: FormValues) => {
    setLoading(true)

    getCaptcha('190072089')
      .then((result) => {
        const ticket = result.ticket
        const randstr = result.randstr

        if (shouldUseFaceId) {
          Dialog.confirm({
            content: '为了保障您的信息安全，接下来需要进行人脸识别验证，请确保光线充足并正对摄像头。',
            confirmText: '开始验证',
            cancelText: '取消',
            onConfirm: async () => {
              handleFaceIdAuth(values, ticket, randstr)
            },
            onCancel: () => {
              setLoading(false)
            },
          })
          return
        }

        handleThreeFactorCheck(values, ticket, randstr)
      })
      .catch(() => {
        setLoading(false)
        Toast.show('请完成验证后再次尝试')
      })
  }

  return {
    loading,
    createOrder,
  }
}
